import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import useIntersectionObserver from "@/hooks/useIntersectionObserver";
import { MapPin, TrendingUp, Shield, Award, Phone, Calculator, TreePine, Home as HomeIcon, X, CheckCircle2, Star, Users, Play, LayoutDashboard } from "lucide-react";
import { ROICalculator } from "@/components/ROICalculator";
import { supabase } from "@/integrations/supabase/client";
import SEO from "@/components/SEO";
import VentureCard from "@/components/VentureCard";
import ScrollToTop from "@/components/ScrollToTop";
import AIChatWidget from "@/components/AIChatWidget";
import ScrollProgressBar from "@/components/ScrollProgressBar";
import EngagementBanner from "@/components/EngagementBanner";
import LiveVisitorCounter from "@/components/LiveVisitorCounter";
// Lead capture components
import LPStickyFooterCTA from "@/components/landing/LPStickyFooterCTA";
import LPExitIntentPopup from "@/components/landing/LPExitIntentPopup";
import LPSocialProofToast from "@/components/landing/LPSocialProofToast";
import LPFaqSection from "@/components/landing/LPFaqSection";

const heroImage = "/images/hero-real-estate.jpg";
import gprExoticaVenture from "@/assets/gpr-exotica-venture.webp";
import gprExoticaVenture400 from "@/assets/gpr-exotica-venture-400w.webp";
import gprKuberaKshetramVenture from "@/assets/gpr-kubera-kshetram-venture.webp";
import gprKuberaKshetramVenture400 from "@/assets/gpr-kubera-kshetram-venture-400w.webp";
import sreeAmbikaNagarVenture from "@/assets/sree-ambika-nagar-venture.webp";
import sreeAmbikaNagarVenture400 from "@/assets/sree-ambika-nagar-venture-400w.webp";
import smartCityAmangalVenture from "@/assets/smart-city-amangal-venture.webp";
import smartCityAmangalVenture400 from "@/assets/smart-city-amangal-venture-400w.webp";
import sreeSaiNagarVenture from "@/assets/sree-sai-nagar-venture.webp";
import sreeSaiNagarVenture400 from "@/assets/sree-sai-nagar-venture-400w.webp";
import easternMeadowsVenture from "@/assets/eastern-meadows-venture.webp";
import easternMeadowsVenture400 from "@/assets/eastern-meadows-venture-400w.webp";

const Home = () => {
  const {
    toast
  } = useToast();
  const [showLeadForm, setShowLeadForm] = useState(true);
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    project: ""
  });
  
  const handleHeroImageLoad = useCallback(() => {
    setHeroImageLoaded(true);
  }, []);

  // Intersection observers for scroll animations
  const { ref: benefitsRef, isVisible: benefitsVisible } = useIntersectionObserver({ threshold: 0.1 });
  const { ref: venturesRef, isVisible: venturesVisible } = useIntersectionObserver({ threshold: 0.1 });
  const { ref: testimonialsRef, isVisible: testimonialsVisible } = useIntersectionObserver({ threshold: 0.1 });
  const handleLeadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast({
        title: "Missing Information",
        description: "Please fill in your name and phone number",
        variant: "destructive"
      });
      return;
    }

    // Save lead to database
    try {
      const { error } = await supabase.from("leads").insert({
        name: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        email: `${formData.phone.replace(/\D/g, '')}@lead.gprinfra.com`,
        interest_product: formData.project || "General Inquiry",
        source: "Website",
        status: "new",
        priority: "warm"
      });
      if (error) {
        console.error("Lead save error:", error);
      }
    } catch (error) {
      console.error("Lead save error:", error);
    }

    // Send email
    try {
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-form-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'quick-inquiry',
          name: formData.name,
          phone: formData.phone,
          project: formData.project
        })
      });
    } catch (error) {
      console.error("Email send error:", error);
    }

    // Open WhatsApp
    const message = `Hi, I'm ${formData.name}. I'm interested in ${formData.project || "your plot investments"}. Please contact me.`;
    const whatsappUrl = `https://wa.me/918297978080?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    toast({
      title: "Inquiry Submitted",
      description: "We'll get back to you shortly!"
    });
    setFormData({
      name: "",
      phone: "",
      project: ""
    });
  };
  const benefits = [{
    icon: HomeIcon,
    title: "₹50,000 Booking",
    description: "Start your investment with just ₹50,000 booking amount",
    color: "primary"
  }, {
    icon: TrendingUp,
    title: "Rental Income",
    description: "Earn guaranteed rental income of ₹10,000/year from your plot investment",
    color: "success"
  }, {
    icon: TreePine,
    title: "Teak Plantation",
    description: "Additional returns from teak plantation of ₹10+ lakhs on the yield",
    color: "warning"
  }, {
    icon: Award,
    title: "Fast Appreciation",
    description: "Due to its infrastructural advantages like RRR, Bhadrachalam Highway, Yadadri to Bhadrachalam 4 liner road, and its prime locations ensuring rapid property value growth",
    color: "info"
  }];
  const ventures = [{
    name: "GPR Exotica",
    location: "Near RRR, Mothukur",
    price: "₹5,800/sq.yd",
    features: ["NALA Converted", "Yadadri Bhadradri Highway 500mtrs", "Premium Amenities"],
    image: gprExoticaVenture,
    imageSmall: gprExoticaVenture400
  }, {
    name: "GPR Kubera Kshetram",
    location: "Mothukur, Yadadri - Bhadradri Highway",
    price: "₹5,600/sq.yd",
    features: ["NALA Converted", "Highway Facing", "Premium Location", "High ROI"],
    image: gprKuberaKshetramVenture,
    imageSmall: gprKuberaKshetramVenture400
  }, {
    name: "Sree Ambika Nagar",
    location: "Near RRR, Pantangi",
    price: "₹18,500/sq.yd",
    features: ["HMDA & RERA Approved", "Highway Facing", "Premium Amenities"],
    image: sreeAmbikaNagarVenture,
    imageSmall: sreeAmbikaNagarVenture400
  }, {
    name: "Sree Sai Nagar",
    location: "Sagar Highway, Chintapalli",
    price: "₹6,500/sq.yd",
    features: ["DTCP Approved", "Near Sri Sai Baba Temple", "Gated Community"],
    image: sreeSaiNagarVenture,
    imageSmall: sreeSaiNagarVenture400
  }, {
    name: "Smart City Amangal",
    location: "Srishailam Highway",
    price: "₹9,000/sq.yd",
    features: ["DTCP Approved", "Near RRR", "Premium Location"],
    image: smartCityAmangalVenture,
    imageSmall: smartCityAmangalVenture400
  }, {
    name: "Eastern Meadows",
    location: "Near Ramoji Filmcity",
    price: "₹32,000/sq.yd",
    features: ["HMDA Approved", "Commercial Potential", "High Returns"],
    image: easternMeadowsVenture,
    imageSmall: easternMeadowsVenture400
  }];
  const testimonials = [{
    name: "Rajesh Kumar",
    role: "Businessman, Hyderabad",
    text: "Best investment decision! The rental income and appreciation have exceeded my expectations.",
    rating: 5
  }, {
    name: "Priya Sharma",
    role: "Software Engineer",
    text: "GPR Infra delivered exactly what they promised. Professional team and transparent process.",
    rating: 5
  }, {
    name: "Venkat Reddy",
    role: "Investor",
    text: "The teak plantation returns are an amazing bonus. Highly recommend GPR Infra!",
    rating: 5
  }];
  return <>
    <SEO
      title="GPR Exotica & Kubera Kshetram | Open Plots in Hyderabad, Telangana, AP"
      description="Buy GPR Exotica & GPR Kubera Kshetram - Premium HMDA approved open plots in Hyderabad, Telangana & Andhra Pradesh. Farmland Narayankhed available. Call 8297978080."
      keywords="GPR Exotica, GPR Kubera Kshetram, open plots Hyderabad, plots Telangana, plots Andhra Pradesh, farmland Narayankhed, farmland Sangareddy, HMDA plots, DTCP plots, RRR plots, ORR plots, Gachibowli, Hitec City, Shamshabad, Kompally, Ghatkesar, Secunderabad, Choutuppal, Shadnagar, investment plots, EMI plots, My Home Group, Aparna Constructions, Ramky Estates, Suchirindia, Myscape, Modi Builders, Vertex Homes, Cybercity, Prestige Group, Sobha, Brigade, Godrej Properties, DLF, Tata Housing, Vasavi Group, Jayabheri, Lansum, Aliens Group, Rajapushpa, SMR Holdings, Muppa Projects, Sumadhura, NCC Urban, Janapriya, EIPL, Praneeth Group, Sree Hemadurga, Aparna Serene, My Home Bhooja, Ramky Discovery, Sobha Dream Acres, Prestige High Fields, Brigade Gateway, Green Valley, Palm Springs, Silver Oak, Golden Gate, Royal Gardens, Emerald Hills, Diamond City, Pearl City, Sapphire Heights, Sunrise Valley, Lake View, Hill Top, Rose Gardens, Lotus Gardens, Oak Gardens, Teak Gardens, Smart City, Cyber City, Paradise City, Fortune City, The Elite, The Premium, The Royal, Skyview, Panorama, Zenith, Apex, Summit, Pinnacle, Infinity, Serenity, Sri Lakshmi Nagar, Sri Sai Nagar, Kubera Kshetram, Green Farms, Teak Farms, Narayankhed Farms, ORR Gateway, RRR Gateway, Vijayawada Developers, Amaravati Properties, Guntur Developers"
      canonicalUrl="https://www.gprhyd.com/"
      structuredData={{
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "LocalBusiness",
            "@id": "https://www.gprhyd.com/#organization",
            "name": "GPR Infra",
            "image": "https://www.gprhyd.com/og-image.png",
            "url": "https://www.gprhyd.com",
            "telephone": "+91-8297978080",
            "email": "gprinfrahyd@gmail.com",
            "priceRange": "₹₹",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Indra Nagendra Theatre, Sri Vishnu Complex, 4th Floor, B-Block, Kharmanghat",
              "addressLocality": "Hyderabad",
              "addressRegion": "Telangana",
              "postalCode": "500035",
              "addressCountry": "IN"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": "17.3456",
              "longitude": "78.5522"
            },
            "openingHoursSpecification": {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
              "opens": "09:00",
              "closes": "19:00"
            },
            "sameAs": [
              "https://www.facebook.com/gprinfra",
              "https://www.instagram.com/gprinfra"
            ]
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is the minimum booking amount for GPR Infra plots?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can book a plot with GPR Infra starting from just ₹50,000. We offer flexible EMI plans from ₹12,000/month."
                }
              },
              {
                "@type": "Question",
                "name": "Are GPR Infra plots DTCP/HMDA approved?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, all GPR Infra projects are either DTCP approved, HMDA approved, or RERA registered with clear titles and proper documentation."
                }
              },
              {
                "@type": "Question",
                "name": "What is the rental income scheme offered by GPR Infra?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "GPR Infra offers a guaranteed rental income of ₹10,000 per year on select plot investments, providing additional passive income."
                }
              },
              {
                "@type": "Question",
                "name": "Where are GPR Infra projects located?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "GPR Infra projects are strategically located near RRR (Regional Ring Road), Yadadri, Pantangi, and along the Vijayawada Highway corridor for maximum appreciation."
                }
              },
              {
                "@type": "Question",
                "name": "What additional benefits do GPR Infra plots offer?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Besides rental income, GPR Infra plots come with teak plantation that can yield returns of ₹10+ lakhs. All projects include modern amenities like gated community, 24/7 security, and landscaped gardens."
                }
              }
            ]
          }
        ]
      }}
    />
    <div className="flex flex-col min-h-screen">
      {/* Hero Section - Premium Redesign */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden bg-gradient-to-br from-background via-background to-primary/5">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          {/* Main hero image */}
          {!heroImageLoaded && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 animate-pulse" />
          )}
          <img 
            src={heroImage} 
            alt="Premium plots near Hyderabad" 
            fetchPriority="high"
            decoding="async"
            width={1920}
            height={1280}
            onLoad={handleHeroImageLoad}
            className={`w-full h-full object-cover transition-opacity duration-700 ${heroImageLoaded ? 'opacity-100' : 'opacity-0'}`}
          />
          {/* Premium gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5" />
        </div>
        
        {/* Floating decorative elements */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 right-1/3 w-64 h-64 bg-secondary/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        
        <div className="container mx-auto relative z-10 py-16 md:py-24 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left Content - Enhanced */}
            <div className="max-w-2xl animate-fade-in">
              {/* Trust badge */}
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 mb-6 backdrop-blur-sm">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-accent text-accent" />
                  ))}
                </div>
                <span className="text-sm font-semibold text-foreground">10+ Years of Trust</span>
              </div>
              
              {/* Main heading with gradient */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-[1.05] tracking-tight">
                Open Plots &
                <span className="block mt-2 bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient-flow bg-[length:200%_200%]">
                  Farmland for Sale
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-2 font-medium text-muted-foreground">
                  in Hyderabad
                </span>
              </h1>
              
              {/* Value proposition */}
              <p className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed max-w-xl">
                DTCP & HMDA approved plots with <span className="text-primary font-semibold">₹10,000/year rental</span>, 
                <span className="text-accent font-semibold"> Teak plantation returns</span>, and exceptional appreciation.
              </p>
              
              {/* Key Stats - Premium Cards */}
              <div className="grid grid-cols-3 gap-3 md:gap-4 mb-8">
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity" />
                  <div className="relative text-center p-4 md:p-5 rounded-2xl bg-card/90 backdrop-blur border border-primary/20">
                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary">₹50K</div>
                    <div className="text-xs md:text-sm text-muted-foreground font-medium">Booking</div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-success to-success/60 rounded-2xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity" />
                  <div className="relative text-center p-4 md:p-5 rounded-2xl bg-card/90 backdrop-blur border border-success/20">
                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-success">₹10K</div>
                    <div className="text-xs md:text-sm text-muted-foreground font-medium">Rent/Year</div>
                  </div>
                </div>
                <div className="relative group">
                  <div className="absolute inset-0 bg-gradient-to-br from-accent to-warning rounded-2xl blur-sm opacity-50 group-hover:opacity-75 transition-opacity" />
                  <div className="relative text-center p-4 md:p-5 rounded-2xl bg-card/90 backdrop-blur border border-accent/20">
                    <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-accent">6+</div>
                    <div className="text-xs md:text-sm text-muted-foreground font-medium">Projects</div>
                  </div>
                </div>
              </div>
              
              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 mb-8">
                <Button size="lg" className="btn-gradient gap-2 h-14 px-8 text-base rounded-xl shadow-xl hover:shadow-2xl transition-all" asChild>
                  <a href="tel:8297978080">
                    <Phone className="h-5 w-5" />
                    Call: 8297978080
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="h-14 px-8 text-base rounded-xl border-2 hover:bg-primary hover:text-primary-foreground hover:border-primary transition-all group" asChild>
                  <Link to="/projects">
                    <Play className="h-5 w-5 mr-2 group-hover:animate-pulse" />
                    Explore Projects
                  </Link>
                </Button>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-4 md:gap-6 pt-6 border-t border-border/50">
                <div className="flex items-center gap-2 bg-success/10 px-4 py-2 rounded-full">
                  <Shield className="h-5 w-5 text-success" />
                  <span className="text-sm font-medium text-foreground">DTCP Approved</span>
                </div>
                <div className="flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full">
                  <Award className="h-5 w-5 text-primary" />
                  <span className="text-sm font-medium text-foreground">HMDA Approved</span>
                </div>
                <div className="flex items-center gap-2 bg-accent/10 px-4 py-2 rounded-full">
                  <Users className="h-5 w-5 text-accent" />
                  <span className="text-sm font-medium text-foreground">1000+ Happy Investors</span>
                </div>
              </div>
            </div>

            {/* Lead Capture Form - Premium Design */}
            {showLeadForm && (
              <div id="book-site-visit" className="w-full max-w-md mx-auto lg:mx-0 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                <Card className="relative overflow-hidden border-0 shadow-2xl bg-card/95 backdrop-blur-xl">
                  {/* Animated gradient border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-primary via-accent to-secondary opacity-20 animate-gradient-flow bg-[length:200%_200%]" />
                  <div className="absolute inset-[1px] bg-card rounded-[inherit]" />
                  
                  {/* Gold accent top border */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary via-accent to-primary" />
                  
                  <CardContent className="relative p-6 md:p-8">
                    <div className="flex justify-end mb-2">
                      <Button variant="ghost" size="icon" className="h-7 w-7 rounded-full hover:bg-destructive/10" onClick={() => setShowLeadForm(false)} aria-label="Close inquiry form">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-4 shadow-lg">
                        <Phone className="h-7 w-7 text-primary-foreground" />
                      </div>
                      <h2 className="text-xl font-bold text-foreground mb-2">
                        Get Expert Guidance
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Free consultation from our property experts
                      </p>
                      <Badge className="mt-2 bg-accent/20 text-accent border-accent/30">
                        ₹50,000 Off - Limited Offer
                      </Badge>
                    </div>
                  
                    <form onSubmit={handleLeadSubmit} className="space-y-4">
                      <Input 
                        type="text" 
                        placeholder="Your Name *" 
                        value={formData.name} 
                        onChange={e => setFormData({ ...formData, name: e.target.value })} 
                        className="h-12 text-base rounded-xl border-2 focus:border-primary bg-background/50" 
                        required 
                      />
                      
                      <Input 
                        type="tel" 
                        placeholder="Phone Number *" 
                        value={formData.phone} 
                        onChange={e => setFormData({ ...formData, phone: e.target.value })} 
                        className="h-12 text-base rounded-xl border-2 focus:border-primary bg-background/50" 
                        pattern="[0-9]{10}" 
                        required 
                      />
                      
                      <Input 
                        type="text" 
                        placeholder="Interested Project (Optional)" 
                        value={formData.project} 
                        onChange={e => setFormData({ ...formData, project: e.target.value })} 
                        className="h-12 text-base rounded-xl border-2 focus:border-primary bg-background/50" 
                      />

                      <Button type="submit" className="w-full h-14 text-base font-bold btn-gradient rounded-xl gap-2 shadow-lg hover:shadow-xl transition-all">
                        <Phone className="h-5 w-5" />
                        Request Free Callback
                      </Button>
                      
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-border" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-card text-muted-foreground">or call directly</span>
                        </div>
                      </div>
                      
                      <Button type="button" variant="outline" className="w-full h-12 text-base rounded-xl border-2 gap-2 hover:bg-primary/10" asChild>
                        <a href="tel:8297978080">
                          <Phone className="h-4 w-4" />
                          8297978080
                        </a>
                      </Button>
                    </form>
                    
                    <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-success" />
                      <span>100% Free | No Spam | Callback in 30 mins</span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
      </section>

      {/* Benefits Section */}
      <section 
        ref={benefitsRef as React.RefObject<HTMLElement>}
        className="py-12 md:py-16 bg-secondary/30"
      >
        <div className="container mx-auto">
          <div className={`text-center mb-8 md:mb-10 transition-all duration-700 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Why Choose GPR Infra?</h2>
            <p className="text-muted-foreground text-base md:text-lg">Unique benefits that multiply your wealth</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {benefits.map((benefit, index) => {
            const colorClasses = {
              primary: "bg-primary/10 text-primary",
              success: "bg-success/10 text-success",
              warning: "bg-warning/10 text-warning",
              info: "bg-info/10 text-info"
            };
            const bgClass = colorClasses[benefit.color as keyof typeof colorClasses].split(' ')[0];
            const textClass = colorClasses[benefit.color as keyof typeof colorClasses].split(' ')[1];
            return <Card 
                key={index} 
                className={`border-border hover:shadow-lg transition-all duration-500 ${benefitsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: benefitsVisible ? `${index * 100}ms` : '0ms' }}
              >
                  <CardContent className="p-6 text-center">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-full ${bgClass} flex items-center justify-center`}>
                      <benefit.icon className={`h-8 w-8 ${textClass}`} />
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
      </section>

      {/* Premium Ventures */}
      <section 
        ref={venturesRef as React.RefObject<HTMLElement>}
        className="py-12 md:py-16"
      >
        <div className="container mx-auto">
          <div className={`text-center mb-8 md:mb-10 transition-all duration-700 ${venturesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">Premium Ventures</h2>
            <p className="text-muted-foreground text-base md:text-lg">Handpicked locations for maximum returns</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {ventures.map((venture, index) => (
              <div 
                key={index}
                className={`transition-all duration-500 ${venturesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: venturesVisible ? `${index * 100}ms` : '0ms' }}
              >
                <VentureCard
                  name={venture.name}
                  location={venture.location}
                  price={venture.price}
                  features={venture.features}
                  image={venture.image}
                  imageSmall={venture.imageSmall}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Calculator CTA */}
      <section className="py-12 md:py-16 bg-gradient-to-r from-primary to-primary/80">
        <div className="container mx-auto text-center">
          <Calculator className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 md:mb-6 text-primary-foreground" />
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary-foreground mb-3 md:mb-4">
            Calculate Your Returns
          </h2>
          <p className="text-primary-foreground/90 text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Use our investment calculator to see potential rental income, teak returns, 
            and appreciation over time
          </p>
          <ROICalculator>
            <Button size="lg" variant="secondary" className="gap-2">
              <Calculator className="h-5 w-5" />
              Calculate Now
            </Button>
          </ROICalculator>
        </div>
      </section>

      {/* Testimonials */}
      <section 
        ref={testimonialsRef as React.RefObject<HTMLElement>}
        className="py-12 md:py-16"
      >
        <div className="container mx-auto">
          <div className={`text-center mb-8 md:mb-10 transition-all duration-700 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-3">What Our Clients Say</h2>
            <p className="text-muted-foreground text-base md:text-lg">Trusted by thousands of happy investors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className={`border-border transition-all duration-500 ${testimonialsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: testimonialsVisible ? `${index * 150}ms` : '0ms' }}
              >
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <span key={i} className="text-accent">★</span>)}
                  </div>
                  <p className="text-foreground mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-16 bg-muted">
        <div className="container mx-auto text-center">
          <Shield className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 md:mb-6 text-primary" />
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
            Ready to Start Your Investment Journey?
          </h2>
          <p className="text-foreground/70 text-sm md:text-base lg:text-lg mb-6 md:mb-8 max-w-2xl mx-auto px-4">
            Join thousands of satisfied investors who chose GPR Infra for their plot investments
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild className="gap-2">
              <a href="https://wa.me/918297978080?text=Hi%20I%20am%20interested%20in%20your%20real%20estate%20projects" target="_blank" rel="noopener noreferrer">
                <Phone className="h-5 w-5" />
                Contact Us on WhatsApp
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/contact">
                Schedule a Visit
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <LPFaqSection language="en" />
    </div>
    <ScrollToTop />
    <AIChatWidget type="public" />
    <LPExitIntentPopup projectName="GPR Infra plots" />
    <LPStickyFooterCTA projectName="GPR Infra plots" />
    <LPSocialProofToast projectName="GPR Infra" />
    <ScrollProgressBar />
    <EngagementBanner />
    <LiveVisitorCounter />
  </>;
};
export default Home;